import {
  type BuyerMaterialSpecificationAdminListItem,
  MaterialComponentCategory,
  type SimplePackagingSpecification,
} from 'api';
import { MaterialComponentValueList } from 'features';
import { formatPackedOn, formatStorageType, formatStrapping } from 'form';
import { Link } from 'react-router-dom';
import { Box, Button, capitalizeString, ShowMore, Stack, Typography, TypographyIcon } from 'ui';
import { Inventory2Outlined, Notes } from 'ui/icons';

type Props = {
  buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem;
};

const MAX_SUMMARY_LENGTH = 100;

const formatSimplePackagingSpecification = (spec: SimplePackagingSpecification) => {
  if (spec.title) {
    return spec.title;
  }
  if (!spec.packed_on) {
    return `${formatStorageType(spec.storage_type)} with ${spec.strapping.map((strapping) => formatStrapping(strapping)).join(', ')}`;
  }
  return (
    spec.title ||
    `${formatStorageType(spec.storage_type)} on ${formatPackedOn(spec.packed_on)} with ${spec.strapping.map((strapping) => formatStrapping(strapping)).join(', ')}`
  );
};

const extractMaterialPackagingSpecifications = (
  packagingSpecifications: BuyerMaterialSpecificationAdminListItem['packaging_specifications']
): string => packagingSpecifications.map((spec) => formatSimplePackagingSpecification(spec)).join(', ');

const getBuyerMaterialSpecificationTitle = (buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem) => {
  let title = `${buyerMaterialSpec.material_grade.name} ${buyerMaterialSpec.material.title} ${capitalizeString(buyerMaterialSpec.material_form.type)}`;
  if (buyerMaterialSpec.material_colour) {
    title = `${title} ${capitalizeString(buyerMaterialSpec.material_colour.name)}`;
  }
  return title;
};

export const BuyerMaterialSpecificationItem = ({ buyerMaterialSpec }: Props) => {
  const materialQualities = buyerMaterialSpec.material_component_values.filter(
    (materialComponent) =>
      materialComponent.supported_component.component.category === MaterialComponentCategory.MATERIAL_QUALITY
  );

  const materialPackagingSpecifications = extractMaterialPackagingSpecifications(
    buyerMaterialSpec.packaging_specifications
  );

  return (
    <Box
      sx={{ backgroundColor: 'background.default' }}
      display="flex"
      flexDirection="column"
      borderRadius={2}
      padding={2}
      height="100%"
      justifyContent="space-between"
    >
      <Stack spacing={2.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="bold">{getBuyerMaterialSpecificationTitle(buyerMaterialSpec)}</Typography>
          {buyerMaterialSpec.file_url ? (
            <Button
              component="a"
              href={buyerMaterialSpec.file_url}
              target="_blank"
              aria-label="download"
              variant="secondary"
            >
              Download Spec File
            </Button>
          ) : null}
        </Stack>
        <MaterialComponentValueList materialComponents={materialQualities} />

        <ShowMore
          text={materialPackagingSpecifications || 'None added'}
          textMarginLeft={4}
          maxLength={MAX_SUMMARY_LENGTH}
          label={<TypographyIcon icon={Inventory2Outlined}>Packaging specifications:</TypographyIcon>}
        />

        <ShowMore
          text={buyerMaterialSpec.notes || 'None added'}
          textMarginLeft={4}
          maxLength={MAX_SUMMARY_LENGTH}
          label={<TypographyIcon icon={Notes}>Notes:</TypographyIcon>}
        />
      </Stack>
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button fullWidth variant="secondary" component={Link} to={buyerMaterialSpec.id.toString()}>
          Edit
        </Button>
      </Box>
    </Box>
  );
};
